import React, { useState, useRef, useEffect } from 'react';
import { utils } from '../utils/api';

function DropZone({ onFileSelect }) {
  const [isDragOver, setIsDragOver] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [expiryDays, setExpiryDays] = useState(5); // Default to 5 days
  const [subscriptionTier, setSubscriptionTier] = useState(null);
  const fileInputRef = useRef(null);
  const folderInputRef = useRef(null);
  const uploadButtonsRef = useRef(null);
  const isOpeningPickerRef = useRef(false);
  const [folderMode, setFolderMode] = useState(false); // Track if we're in folder mode

  // Fetch subscription tier for current user (paid vs free)
  useEffect(() => {
    (async () => {
      try {
        const userId = localStorage.getItem('fileshare_user_id');
        if (!userId) return;
        const apiBase = process.env.REACT_APP_API_URL || 'https://fileshare-backend.couriertrackerinfo.workers.dev';
        const res = await fetch(`${apiBase}/api/user/profile?userId=${encodeURIComponent(userId)}`);
        if (!res.ok) return;
        const json = await res.json();
        const tier = json?.profile?.subscriptionTier || null;
        console.log('DropZone API response:', json);
        console.log('DropZone subscription tier:', tier);
        setSubscriptionTier(tier);
        // If paid, default to no expiry
        if (tier && tier.tier && tier.tier !== 'free') {
          console.log('Premium user detected, setting expiry to -1');
          setExpiryDays(-1);
        }
      } catch (_) {}
    })();
  }, []);

  const openFilePicker = () => {
    if (isOpeningPickerRef.current) return;
    isOpeningPickerRef.current = true;
    if (fileInputRef.current) {
      setFolderMode(false);
      fileInputRef.current.value = '';
      fileInputRef.current.click();
    }
    setTimeout(() => { isOpeningPickerRef.current = false; }, 300);
  };

  const openFolderPicker = () => {
    if (isOpeningPickerRef.current) return;
    isOpeningPickerRef.current = true;
    if (folderInputRef.current) {
      setFolderMode(true);
      folderInputRef.current.value = '';
      folderInputRef.current.click();
    }
    setTimeout(() => { isOpeningPickerRef.current = false; }, 300);
  };

  // Calculate dynamic expiry options based on subscription and total file size
  const getExpiryDaysOptions = () => {
    console.log('getExpiryDaysOptions called with subscriptionTier:', subscriptionTier);
    // Premium users: no expiry (files stay until subscription ends)
    if (subscriptionTier && subscriptionTier.tier && subscriptionTier.tier !== 'free') {
      console.log('Premium user detected in getExpiryDaysOptions, returning no expiry');
      return [
        { value: -1, label: 'No expiry (Premium)' },
      ];
    }
    console.log('Free user detected, returning regular expiry options');

    const totalSize = selectedFiles.reduce((sum, file) => sum + file.size, 0);
    const sizeGB = totalSize / (1024 * 1024 * 1024);
    const sizeMB = totalSize / (1024 * 1024);
    
    // New rule: files under 100MB allow 12hrs, 1, 2, 3, 5, 10, 15 days
    if (sizeMB > 0 && sizeMB < 100) {
      return [
        { value: 0.5, label: '12 hours' },
        { value: 1, label: '1 day' },
        { value: 2, label: '2 days' },
        { value: 3, label: '3 days' },
        { value: 5, label: '5 days' },
        { value: 10, label: '10 days' },
        { value: 15, label: '15 days (max)' }
      ];
    }

    // Files > 10GB: restrict to 6h, 12h, 18h, 24h
    if (sizeGB > 10) {
      return [
        { value: 0.25, label: '6 hours' },
        { value: 0.5, label: '12 hours' },
        { value: 0.75, label: '18 hours' },
        { value: 1, label: '24 hours' }
      ];
    }

    // Files 100MB-10GB: 12hrs, 1, 2, 3 days (max 3 days)
    return [
      { value: 0.5, label: '12 hours' },
      { value: 1, label: '1 day' },
      { value: 2, label: '2 days' },
      { value: 3, label: '3 days (max)' }
    ];
  };

  // Ensure selected expiry is valid for current file size
  const currentOptions = getExpiryDaysOptions();
  const validExpiryValues = currentOptions.map(opt => opt.value);
  if (!validExpiryValues.includes(expiryDays)) {
    // Reset to the maximum allowed value for current file size
    // For paid users, prefer 10 days default if not present
    if (subscriptionTier && subscriptionTier.tier && subscriptionTier.tier !== 'free') {
      if (expiryDays !== 10) setExpiryDays(10);
    } else {
      const maxValue = Math.max(...validExpiryValues);
      if (expiryDays !== maxValue) {
        setExpiryDays(maxValue);
      }
    }
  }

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragOver(false);
    
    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      handleFileSelection(files);
    }
  };


  const handleFileSelection = (files) => {
    // Basic file validation
    // No hard per-file or total size limit here; server-side bandwidth quota will enforce usage

    const validFiles = [];

    for (const file of files) {
      validFiles.push(file);
    }

    if (validFiles.length > 0) {
      setSelectedFiles(validFiles);
    }
  };

  const handleUpload = () => {
    if (selectedFiles.length > 0 && onFileSelect) {
      onFileSelect(selectedFiles, expiryDays);
      setSelectedFiles([]);
    }
  };

  const handleCancel = () => {
    setSelectedFiles([]);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };


  const removeFile = (index) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const getTotalSize = () => {
    return selectedFiles.reduce((total, file) => total + file.size, 0);
  };

  // IDE-style file icon
  const FileIcon = () => (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
      <path d="M9.5 1H3a1 1 0 0 0-1 1v12a1 1 0 0 0 1 1h10a1 1 0 0 0 1-1V4.5L9.5 1ZM3 0a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V4.293a1 1 0 0 0-.293-.707l-4-4A1 1 0 0 0 10 3H3Z"/>
    </svg>
  );

  const UploadIcon = () => (
    <svg width="32" height="32" viewBox="0 0 24 24" fill="currentColor">
      <path d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z"/>
      <path d="M12,11L16,15H13.5V19H10.5V15H8L12,11Z"/>
    </svg>
  );

  return (
    <div className="w-full">
      {window.__bandwidthBlocked?.active ? (
        <div style={{
          border: '1px solid #ef4444',
          borderRadius: '2px',
          backgroundColor: '#1a1d29',
          padding: '24px',
          color: '#e2e8f0',
          textAlign: 'center'
        }}>
          <h3 style={{ margin: 0, marginBottom: '8px' }}>Bandwidth limit reached</h3>
          <p style={{ margin: 0, marginBottom: '12px', color: '#9ca3af' }}>Upgrade your plan to continue uploading.</p>
          <button onClick={() => (window.location.href = '/pricing')} style={{
            backgroundColor: '#3b82f6', color: 'white', padding: '8px 16px', border: 'none', borderRadius: '2px', cursor: 'pointer'
          }}>Upgrade Plan</button>
        </div>
      ) : window.__storageBlocked?.active ? (
        <div style={{
          border: '1px solid #ef4444',
          borderRadius: '2px',
          backgroundColor: '#1a1d29',
          padding: '24px',
          color: '#e2e8f0',
          textAlign: 'center'
        }}>
          <h3 style={{ margin: 0, marginBottom: '8px' }}>Storage limit reached</h3>
          <p style={{ margin: 0, marginBottom: '12px', color: '#9ca3af' }}>
            {window.__storageBlocked.details?.isPremium 
              ? 'You\'ve reached your 500GB storage limit. Additional storage will be charged at $0.016 per GB.'
              : 'You\'ve reached your 100GB storage limit. Upgrade to Premium for 500GB storage.'
            }
          </p>
          <button onClick={() => (window.location.href = '/pricing')} style={{
            backgroundColor: '#3b82f6', color: 'white', padding: '8px 16px', border: 'none', borderRadius: '2px', cursor: 'pointer'
          }}>
            {window.__storageBlocked.details?.isPremium ? 'View Billing' : 'Upgrade Plan'}
          </button>
        </div>
      ) : selectedFiles.length === 0 ? (
        <div
          style={{
            border: `2px dashed ${isDragOver ? '#3b82f6' : '#2a2d3a'}`,
            borderRadius: '2px',
            padding: '48px 24px',
            textAlign: 'center',
            cursor: 'pointer',
            transition: 'all 0.2s ease',
            backgroundColor: isDragOver ? '#1e3a8a15' : '#242938'
          }}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <div style={{ color: isDragOver ? '#3b82f6' : '#6b7280', marginBottom: '16px' }}>
              <UploadIcon />
            </div>
            
            <h3 style={{ 
              fontSize: '16px', 
              fontWeight: '500', 
              color: '#e2e8f0', 
              marginBottom: '8px',
              margin: '0 0 8px 0'
            }}>
              {isDragOver ? 'Drop your files here' : 'Upload files'}
            </h3>
            
            <p style={{ 
              fontSize: '12px', 
              color: '#9ca3af', 
              marginBottom: '24px',
              margin: '0 0 24px 0',
              lineHeight: '1.4'
            }}>
              Drag and drop multiple files or a folder here, or click to browse
            </p>
            
            <div ref={uploadButtonsRef} className="upload-buttons" style={{ display: 'flex', gap: '12px' }}>
              <button
                type="button"
                disabled={!!window.__bandwidthBlocked?.active || !!window.__storageBlocked?.active}
                onMouseDown={(e) => {
                  e.stopPropagation();
                  if (e.nativeEvent && typeof e.nativeEvent.stopImmediatePropagation === 'function') {
                    e.nativeEvent.stopImmediatePropagation();
                  }
                }}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  if (!window.__bandwidthBlocked?.active && !window.__storageBlocked?.active) openFilePicker();
                }}
                style={{
                  backgroundColor: '#3b82f6',
                  color: 'white',
                  padding: '8px 16px',
                  borderRadius: '2px',
                  fontSize: '12px',
                  fontWeight: '500',
                  border: 'none',
                  cursor: 'pointer',
                  transition: 'background-color 0.2s ease'
                }}
                onMouseEnter={(e) => e.target.style.backgroundColor = '#2563eb'}
                onMouseLeave={(e) => e.target.style.backgroundColor = '#3b82f6'}
              >
                Choose Files
              </button>
              <button
                type="button"
                disabled={!!window.__bandwidthBlocked?.active || !!window.__storageBlocked?.active}
                onMouseDown={(e) => {
                  e.stopPropagation();
                  if (e.nativeEvent && typeof e.nativeEvent.stopImmediatePropagation === 'function') {
                    e.nativeEvent.stopImmediatePropagation();
                  }
                }}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  if (!window.__bandwidthBlocked?.active && !window.__storageBlocked?.active) openFolderPicker();
                }}
                style={{
                  backgroundColor: 'transparent',
                  color: '#9ca3af',
                  border: '1px solid #374151',
                  padding: '8px 16px',
                  borderRadius: '2px',
                  fontSize: '12px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  e.target.style.borderColor = '#6b7280';
                  e.target.style.color = '#e2e8f0';
                }}
                onMouseLeave={(e) => {
                  e.target.style.borderColor = '#374151';
                  e.target.style.color = '#9ca3af';
                }}
              >
                Choose Folder
              </button>
            </div>
          </div>

          {/* Dedicated inputs: one for files, one for folders (static attributes) */}
          <input
            ref={fileInputRef}
            type="file"
            multiple
            style={{ display: 'none' }}
            onChange={(e) => {
              console.log(`📄 HOME(files): ${e.target.files?.length || 0} selected`);
              if (e.target.files && e.target.files.length > 0) {
                const files = Array.from(e.target.files);
                setFolderMode(false);
                handleFileSelection(files);
                e.target.value = '';
              }
            }}
          />
          <input
            ref={folderInputRef}
            type="file"
            webkitdirectory=""
            directory=""
            mozdirectory=""
            style={{ display: 'none' }}
            onChange={(e) => {
              console.log(`📁 HOME(folder): ${e.target.files?.length || 0} items`);
              if (e.target.files && e.target.files.length > 0) {
                const files = Array.from(e.target.files);
                setFolderMode(true);
                if (files[0].webkitRelativePath) {
                  console.log('📁 HOME: Folder detected:', files[0].webkitRelativePath);
                }
                handleFileSelection(files);
                e.target.value = '';
              }
            }}
          />
        </div>
      ) : (
        <div style={{
          border: '1px solid #374151',
          borderRadius: '2px',
          backgroundColor: '#242938',
          padding: '24px'
        }}>
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'space-between',
            marginBottom: '16px'
          }}>
            <h3 style={{ 
              fontSize: '14px', 
              fontWeight: '500', 
              color: '#e2e8f0',
              margin: '0'
            }}>
              Files to Upload ({selectedFiles.length})
            </h3>
            <span style={{ 
              fontSize: '12px', 
              color: '#9ca3af'
            }}>
              Total: {utils.formatFileSize(getTotalSize())}
            </span>
          </div>

          <div style={{
            maxHeight: '300px',
            overflowY: 'auto',
            marginBottom: '16px',
            border: '1px solid #374151',
            borderRadius: '2px',
            backgroundColor: '#1a1d29'
          }}>
            {selectedFiles.map((file, index) => (
              <div
                key={index}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  padding: '8px 12px',
                  borderBottom: index < selectedFiles.length - 1 ? '1px solid #374151' : 'none',
                  fontSize: '12px'
                }}
              >
                <div style={{ color: '#6b7280', marginRight: '8px' }}>
                  <FileIcon />
                </div>
                <div style={{ flex: '1', minWidth: '0' }}>
                  <div style={{ 
                    color: '#e2e8f0', 
                    fontWeight: '400',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}>
                    {file.name}
                  </div>
                  <div style={{ color: '#9ca3af', fontSize: '11px' }}>
                    {utils.formatFileSize(file.size)}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => removeFile(index)}
                  style={{
                    backgroundColor: 'transparent',
                    border: 'none',
                    color: '#ef4444',
                    cursor: 'pointer',
                    padding: '4px',
                    borderRadius: '2px',
                    fontSize: '12px',
                    marginLeft: '8px'
                  }}
                  title="Remove file"
                >
                  ×
                </button>
              </div>
            ))}
          </div>

          {/* File Expiry Selection */}
          <div style={{
            padding: '12px 16px',
            backgroundColor: '#1a1d29',
            border: '1px solid #374151',
            borderRadius: '2px',
            marginBottom: '16px'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" style={{ color: '#6b7280' }}>
                  <path d="M12,2A10,10 0 0,0 2,12A10,10 0 0,0 12,22A10,10 0 0,0 22,12A10,10 0 0,0 12,2M16.2,16.2L11,13V7H12.5V12.2L17,14.4L16.2,16.2Z"/>
                </svg>
                <span style={{ fontSize: '12px', color: '#9ca3af', fontWeight: '500' }}>
                  Auto-delete after:
                </span>
              </div>
              <select
                value={expiryDays}
                onChange={(e) => setExpiryDays(parseInt(e.target.value))}
                style={{
                  backgroundColor: '#242938',
                  color: '#e2e8f0',
                  border: '1px solid #374151',
                  borderRadius: '2px',
                  padding: '4px 8px',
                  fontSize: '11px',
                  cursor: 'pointer',
                  outline: 'none'
                }}
              >
                {getExpiryDaysOptions().map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              <span style={{ fontSize: '11px', color: '#6b7280' }}>
                Files will be automatically deleted after expiry
              </span>
            </div>
          </div>

          <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
            <button
              type="button"
              onClick={handleCancel}
              style={{
                backgroundColor: 'transparent',
                color: '#9ca3af',
                border: '1px solid #374151',
                padding: '8px 16px',
                borderRadius: '2px',
                fontSize: '12px',
                fontWeight: '500',
                cursor: 'pointer',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => {
                e.target.style.borderColor = '#6b7280';
                e.target.style.color = '#e2e8f0';
              }}
              onMouseLeave={(e) => {
                e.target.style.borderColor = '#374151';
                e.target.style.color = '#9ca3af';
              }}
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleUpload}
              style={{
                backgroundColor: '#3b82f6',
                color: 'white',
                padding: '8px 16px',
                borderRadius: '2px',
                fontSize: '12px',
                fontWeight: '500',
                border: 'none',
                cursor: 'pointer',
                transition: 'background-color 0.2s ease'
              }}
              onMouseEnter={(e) => e.target.style.backgroundColor = '#2563eb'}
              onMouseLeave={(e) => e.target.style.backgroundColor = '#3b82f6'}
            >
              Upload Files
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default DropZone; 